import React, { useEffect, useMemo, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as api from '../../utils/api';
import './EventCheckoutScanner.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Printed sheets encode only this — the full build payload is resolved
// server-side via GET /qrCode/{qrCodeId}.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '—';
    const s = new Date(startDate + 'T00:00:00');
    const sMonth = MONTHS[s.getMonth()];
    const sDay = s.getDate();
    const sYear = s.getFullYear();
    if (!endDate || endDate === startDate) return `${sMonth} ${sDay}, ${sYear}`;
    const e = new Date(endDate + 'T00:00:00');
    const eMonth = MONTHS[e.getMonth()];
    const eDay = e.getDate();
    const eYear = e.getFullYear();
    if (sYear !== eYear) return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${eYear}`;
    if (s.getMonth() === e.getMonth()) return `${sMonth} ${sDay}–${eDay}, ${sYear}`;
    return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${sYear}`;
};

// Local YYYY-MM-DD for "today", so comparisons match the plain startDate
// strings the API returns (avoids UTC/local timezone off-by-one issues).
const todayDateString = () => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
};

const EventCheckoutScanner = ({ token }) => {

    const [step, setStep] = useState('event-select'); // event-select | camera | resolving | confirm | submitting | done
    const [events, setEvents] = useState([]);
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const [cameraError, setCameraError] = useState(null);
    const [decodeError, setDecodeError] = useState(null);
    const [capturing, setCapturing] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null); // { dataUrl, mimeType }
    const [decodedPayload, setDecodedPayload] = useState(null);

    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                setLoadingEvents(true);
                const data = await api.fetchEvents();
                setEvents(data || []);
            } catch {
                toast.error('Failed to load events');
            } finally {
                setLoadingEvents(false);
            }
        })();
    }, []);

    const sortedEvents = useMemo(() => {
        const today = todayDateString();
        return events
            // Hide events that haven't started yet.
            .filter((ev) => !ev.startDate || ev.startDate <= today)
            .sort((a, b) => {
                const da = b.startDate || b.endDate || '';
                const db = a.startDate || a.endDate || '';
                return da.localeCompare(db);
            });
    }, [events]);

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    };

    useEffect(() => () => stopCamera(), []);

    const startCamera = async () => {
        setCameraError(null);
        setDecodeError(null);

        // getUserMedia only exists in a secure context (HTTPS, or localhost).
        // Over plain HTTP the browser never even shows a permission prompt —
        // it just silently withholds the API, which otherwise looks
        // identical to a denied/broken permission.
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
            setCameraError(
                "Camera access requires a secure (HTTPS) connection. This page was loaded over plain HTTP, " +
                "so the browser won't even prompt for camera permission. Load this app over HTTPS and try again."
            );
            return;
        }

        try {
            // "ideal" just asks for the best available up to this cap — it
            // degrades gracefully on cameras that can't do 4K, so raising it
            // only helps on the (increasingly common) devices that can.
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment', width: { ideal: 3840 }, height: { ideal: 2160 } },
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
        } catch (err) {
            console.error(err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setCameraError('Camera permission was denied. Check your browser/site permissions and try again.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setCameraError('No camera was found on this device.');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                setCameraError('The camera is already in use by another app. Close it and try again.');
            } else if (err.name === 'SecurityError') {
                setCameraError("Camera access was blocked because this page isn't loaded over a secure (HTTPS) connection.");
            } else {
                setCameraError('Could not access the camera. Check your browser/site permissions and try again.');
            }
        }
    };

    const handleSelectEvent = async (ev) => {
        setSelectedEvent(ev);
        setStep('camera');
        await startCamera();
    };

    const handleBackToEvents = () => {
        stopCamera();
        setSelectedEvent(null);
        setStep('event-select');
    };

    const handleCapture = async () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;

        setDecodeError(null);
        setCapturing(true);

        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        const qrCodeId = (code?.data || '').trim();

        if (!code || !UUID_RE.test(qrCodeId)) {
            // The decode above is near-instant, so going straight from tap to
            // an error reads as if the button didn't do anything — hold the
            // button's spinner up briefly before swapping in the message.
            await new Promise((r) => setTimeout(r, 400));
            setCapturing(false);
            setDecodeError(
                code
                    ? 'This QR code does not look like a Chronicles character sheet.'
                    : 'No QR code found. Make sure the whole character sheet is in frame, well lit, and steady, then try again.'
            );
            return;
        }

        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setDecodeError(null);
        stopCamera();
        setCapturing(false);
        setStep('resolving');

        try {
            const { payload } = await api.fetchQrCode(qrCodeId);
            if (!payload || !payload.characterId) {
                throw new Error('QR code payload missing characterId');
            }
            setCapturedImage({ dataUrl, mimeType: 'image/jpeg' });
            setDecodedPayload(payload);
            setStep('confirm');
        } catch (err) {
            const notFound = err?.response?.status === 404;
            setDecodeError(
                notFound
                    ? 'This QR code is no longer valid. Ask the player to reprint their character sheet.'
                    : 'Could not look up this QR code. Check your connection and try again.'
            );
            setStep('camera');
            await startCamera();
        }
    };

    const handleRetake = async () => {
        setCapturedImage(null);
        setDecodedPayload(null);
        setDecodeError(null);
        setStep('camera');
        await startCamera();
    };

    const handleSubmit = async () => {
        if (!selectedEvent || !decodedPayload || !capturedImage) return;
        setStep('submitting');
        try {
            await api.submitCheckoutScan({
                eventId: selectedEvent.eventId,
                qrPayload: decodedPayload,
                imageBase64: capturedImage.dataUrl,
                imageMimeType: capturedImage.mimeType,
            });
            setStep('done');
        } catch (err) {
            toast.error(err?.response?.data?.error || 'Failed to submit checkout scan');
            setStep('confirm');
        }
    };

    const handleScanAnother = () => {
        setCapturedImage(null);
        setDecodedPayload(null);
        setDecodeError(null);
        setStep('event-select');
        setSelectedEvent(null);
    };

    return (
        <div className="ecs-container">
            <h1 className="landing-title">Event Checkout</h1>

            {step === 'event-select' && (
                <div className="ecs-panel">
                    <p className="ecs-instructions">Select the event you're checking out from, then photograph your character sheet.</p>
                    {loadingEvents ? (
                        <div className="ecs-loading">Loading events…</div>
                    ) : sortedEvents.length === 0 ? (
                        <div className="ecs-empty">No events found.</div>
                    ) : (
                        <ul className="ecs-event-list">
                            {sortedEvents.map((ev, idx) => (
                                <React.Fragment key={ev.eventId}>
                                    <li
                                        className={`ecs-event-item${idx === 0 ? ' ecs-event-item-current' : ''}`}
                                        onClick={() => handleSelectEvent(ev)}
                                    >
                                        <span className="ecs-event-date">{formatDateRange(ev.startDate, ev.endDate)}</span>
                                        <span className="ecs-event-site">{ev.site}</span>
                                        <span className="ecs-event-type">{ev.eventType}</span>
                                    </li>
                                    {idx === 0 && sortedEvents.length > 1 && (
                                        <li className="ecs-event-divider" aria-hidden="true" />
                                    )}
                                </React.Fragment>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {step === 'camera' && (
                <div className="ecs-panel">
                    <div className="ecs-camera-header">
                        <button className="ecs-btn ecs-btn-secondary" onClick={handleBackToEvents}>← Back</button>
                        <span className="ecs-camera-event">{selectedEvent?.site} — {formatDateRange(selectedEvent?.startDate, selectedEvent?.endDate)}</span>
                    </div>

                    {cameraError ? (
                        <div className="ecs-error">{cameraError}</div>
                    ) : (
                        <div className="ecs-camera-frame">
                            <video ref={videoRef} className="ecs-video" playsInline muted />
                            <div className="ecs-camera-guide" />
                            <p className="ecs-camera-hint">Align your whole character sheet within the frame</p>
                        </div>
                    )}

                    {decodeError && <div className="ecs-error">{decodeError}</div>}

                    <canvas ref={canvasRef} className="ecs-hidden-canvas" />

                    <div className="ecs-actions">
                        <button
                            className={`ecs-btn ecs-btn-primary${capturing ? ' ecs-btn-pressed' : ''}`}
                            onClick={handleCapture}
                            disabled={!!cameraError || capturing}
                        >
                            {capturing ? <span className="ecs-btn-spinner" aria-hidden="true" /> : '📷 Capture'}
                        </button>
                    </div>
                </div>
            )}

            {step === 'resolving' && (
                <div className="ecs-panel">
                    <div className="ecs-loading">Looking up character…</div>
                </div>
            )}

            {step === 'confirm' && (
                <div className="ecs-panel">
                    <h2 className="ecs-subtitle">Confirm Checkout Scan</h2>
                    {capturedImage && (
                        <img src={capturedImage.dataUrl} alt="Captured character sheet" className="ecs-preview-img" />
                    )}
                    <div className="ecs-confirm-details">
                        <div><strong>Event:</strong> {selectedEvent?.site} — {formatDateRange(selectedEvent?.startDate, selectedEvent?.endDate)}</div>
                        <div><strong>Character:</strong> {decodedPayload?.name}</div>
                        {decodedPayload?.subRace && <div><strong>Sub Race:</strong> {decodedPayload.subRace}</div>}
                    </div>
                    <div className="ecs-actions">
                        <button className="ecs-btn ecs-btn-secondary" onClick={handleRetake}>Retake Photo</button>
                        <button className="ecs-btn ecs-btn-primary" onClick={handleSubmit}>Submit Checkout</button>
                    </div>
                </div>
            )}

            {step === 'submitting' && (
                <div className="ecs-panel">
                    <div className="ecs-loading">Submitting…</div>
                </div>
            )}

            {step === 'done' && (
                <div className="ecs-panel">
                    <div className="ecs-success">✅ Checkout scan submitted! Staff will finish processing your checkout.</div>
                    <div className="ecs-actions">
                        <button className="ecs-btn ecs-btn-primary" onClick={handleScanAnother}>Scan Another Character</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventCheckoutScanner;
